import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function PrivacyPolicy() {
  const s = useSiteSettings();
  const email = s.contact_email || "";
  const name = s.business_name || "Gil Maakaroun";
  return (
    <LegalLayout
      title="Privacy Policy"
      seoTitle="Privacy Policy"
      seoDescription={`How ${name} collects, uses, and protects collector information.`}
      intro="How we collect, use, and protect the information you share with us — written for collectors, not lawyers."
      effectiveDate="February 2026"
      testid="privacy-policy-page"
    >
      <h2>Who we are</h2>
      <p>
        This Privacy Policy explains how {name} (&quot;we,&quot; &quot;us,&quot; or
        the &quot;Company&quot;) handles information collected through this
        website and any connected storefronts.
      </p>
      {email && (
        <p>
          Business contact: <a href={`mailto:${email}`}>{email}</a>.
        </p>
      )}

      <h2>What we collect</h2>
      <ul>
        <li><strong>Contact information</strong> you provide when submitting a form: name, email, phone, city and state.</li>
        <li><strong>Order details</strong> such as your missing-sticker list, fulfillment preference (shipping / local pickup / event pickup), and notes.</li>
        <li><strong>Event RSVPs</strong>: name, email, guest count, notes.</li>
        <li><strong>Certificate submissions</strong>: album name, collector display preference (full name, first name + initial, or hidden).</li>
        <li><strong>Consent choices</strong>: whether you agreed to be contacted about a specific request and whether you opted in to marketing email or SMS.</li>
        <li><strong>Technical basics</strong>: standard web-server logs, cookies used to keep you signed in to the admin dashboard.</li>
      </ul>
      <p>
        Purchases made in our Shopify storefront are governed by Shopify&apos;s own
        privacy practices. Payment card details are handled by Shopify&apos;s
        payment processors — we never see or store card numbers.
      </p>

      <h2>How we use it</h2>
      <ul>
        <li>To respond to your request or RSVP.</li>
        <li>To prepare quotes, invoices, and shipments.</li>
        <li>To publish certificate records at the visibility level <em>you</em> chose (full name, first name + initial, or hidden).</li>
        <li>To send marketing email or SMS <strong>only if you opted in</strong>. You can opt out any time.</li>
        <li>To keep an internal audit log of admin actions on your record — for your protection and ours.</li>
      </ul>

      <h2>What we do not do</h2>
      <ul>
        <li>We do not sell your data.</li>
        <li>We do not run third-party advertising on this site.</li>
        <li>We do not use your name on a public certificate unless you asked us to.</li>
        <li>We do not send marketing messages unless you explicitly opted in.</li>
      </ul>

      <h2>Cookies</h2>
      <p>
        We use only strictly necessary cookies (for admin authentication) and
        basic analytics cookies for privacy-respecting usage measurement. No
        third-party advertising cookies.
      </p>

      <h2>Your rights</h2>
      <ul>
        <li><strong>Access</strong> — request a copy of everything we hold about you.</li>
        <li><strong>Correction</strong> — ask us to fix inaccurate information.</li>
        <li><strong>Erasure</strong> — ask us to remove your record. Numbered certificates remain in the public registry with the personal name replaced by &quot;collector removed at request&quot; so the numeric permanence is preserved.</li>
        <li><strong>Opt out</strong> — unsubscribe from any marketing communication at any time.</li>
      </ul>
      {email && (
        <p>
          Requests can be sent to <a href={`mailto:${email}`}>{email}</a>.
          We aim to respond within 14 days.
        </p>
      )}

      <h2>Children</h2>
      <p>
        This service is designed to be family-safe. Where a collector is a
        minor, we ask that a parent or guardian complete forms on their behalf
        and provide consent.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We may update this policy from time to time. The version and effective
        date at the top of this page always reflect the current text.
      </p>
    </LegalLayout>
  );
}
